import { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styles from './result-table-card.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { urls } from '../../../../utils/url-helper/index';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Icon from '../../../atoms/icon';
import Container from 'react-bootstrap/Container';
import Tag from '../../../molecules/screener/tag';
import FundDetailTable from '../fund-detail-table';
import FundDetailButtons from '../fund-detail-buttons';
import NetGrossDisclosureButton from '../net-gross-disclosure-button';
import ButtonIconLink from '../../../atoms/button-icon-link';
import ButtonViewGlossary from '../../../atoms/button-view-glossary';
import { FIELDS, CHANNELS } from '../../../../shared/screener-constants';
import {
  getTextValueColorClass,
  alignData,
  getIsStandardPerformance,
  shouldShowTicker,
  isShowNetGrossExpenseRatioDisclosure
} from '../../../../utils/sharedHelpers';
import useRowStore, { INVEST_BUTTON_MODES } from '../../../../stores/screener/use-row-store';
import ModalInvest from '../../profile/page-header/modals/modal-invest';
import globals from '../../../../utils/globals';
import format from '../../../../utils/format';
import RatingRow from '../morningstar-rating-row';
import Button from 'react-bootstrap/Button';
import DocumentsModal from '../documents-modal';
import DropdownBasic from '../../../atoms/dropdown-basic';
import InteractiveElement from '../../../atoms/interactive-element';
import useSideRailContext from '../../../../stores/profile/use-side-rail-context';
import PerformanceValue from '../../../../components/atoms/performance-value';

const className = augmentClassName(styles);

export const ResultTableCard = ({
  item,
  index,
  isExpanded,
  isSignatureResult,
  onClickExpandRow,
  performanceQuarterly,
  participantHoldings,
  performanceAsOf,
  isSearchedPlanInContext,
  isMobile,
  onChangePerformanceTerm,
  searchedHoldings,
  planInfo,
  showSalesCharge
}) => {
  const [expandTags, setExpandTags] = useState(false);
  const fundType = item[FIELDS.TCFUNDTYPE];
  const fundTicker = item[FIELDS.TCTICKER];
  const productType = item[FIELDS.PRODUCTTYPE];
  const assetClass = item[FIELDS.ASSETCLASS];
  const shareClass = item[FIELDS.TCSHARECLASS];
  const isProp = item[FIELDS.TCTREATASPROP];
  const underlyingFundIncepDate = item[FIELDS.TCUNDERLYINGFUNDINCEPTIONDATE];
  const xid = item[FIELDS.TCWSODISSUE];
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');
  const isStandardPerformance = getIsStandardPerformance(item, performanceQuarterly);
  const sideRail = useSideRailContext();
  const performanceTerms = ['Monthly', 'Quarterly'];
  const showNetGrossDisclosure = isShowNetGrossExpenseRatioDisclosure(
    item[FIELDS.TCNETEXPENSES],
    item[FIELDS.TCGROSSEXPENSE]
  );
  const showSalesChargeData = showSalesCharge && item[FIELDS.TCSHARECLASS] === 'Class A';

  const {
    channel,
    fundDetailTableData,
    investButtonMode,
    isInvestModalVisible,
    isPlanInContext,
    handleClickInvestButton,
    handleCloseInvestModal,
    restrictionText,
    fundName,
    UrlDomain,
    UrlPrefix
  } = useRowStore({
    fundType,
    xid,
    item,
    isSearchedPlanInContext,
    participantHoldings,
    searchedHoldings,
    planInfo
  });

  const {
    oneYearReturn,
    threeYearReturn,
    fiveYearReturn,
    tenYearReturn,
    sinceIncep,
    inceptionDate,
    asOfDate
  } = alignData(item, performanceQuarterly, performanceAsOf);

  const isShowUnderlyingFundIncepDate =
    !isStandardPerformance &&
    underlyingFundIncepDate !== inceptionDate &&
    underlyingFundIncepDate !== null;

  const onClickInvestButton = () => {
    if (investButtonMode === INVEST_BUTTON_MODES.modal) {
      handleClickInvestButton();
    } else if (investButtonMode === INVEST_BUTTON_MODES.link) {
      window.open(globals.investButtonUrl, '_blank');
    }
  };

  const profileUrl = urls.pages.linkToProfile({
    xid: item[FIELDS.TCWSODISSUE],
    channel,
    UrlDomain,
    UrlPrefix
  });

  const joinedRestrictions = restrictionText?.join('<br></br>');

  const tags = expandTags ? item.tags : item.collapseTags;

  const setModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setShowModal(true);
  };

  const inceptionDateOriginal = new Date(inceptionDate);
  const inceptionDate1Yr = new Date(inceptionDate).setFullYear(
    inceptionDateOriginal.getFullYear() + 1
  );
  const formattedAsOfDate = new Date(format.oaDate(asOfDate || 0));

  const isLinkedPerformance = {
    // TCIsLinkedPerf1Yr is set to 0 in Screener 296, add this condition to calculate 1 year link pref on front end,
    1: item[FIELDS.TCISLINKEDPERF1YR] || inceptionDate1Yr > formattedAsOfDate,
    3: item[FIELDS.TCISLINKEDPERF3YR],
    5: item[FIELDS.TCISLINKEDPERF5YR],
    10: item[FIELDS.TCISLINKEDPERF10YR]
  };

  const renderPerformanceElement = (returnValue, year) => {
    const fontStyle = showSalesChargeData ? 'font-italic' : '';
    let disclosureSuperScript = '';
    const value = showSalesChargeData ? returnValue.salesCharge : returnValue.standard;

    /*Checking if returnValue is a number, because 0 is a falsey value */
    const isNumber = format.isValidNumber(returnValue);

    if (isNumber && isLinkedPerformance[year]) {
      disclosureSuperScript = '*';
    }

    return (
      <div {...className('value', fontStyle)}>
        <PerformanceValue value={value} />
        <span {...className('text-dark')}>{disclosureSuperScript} </span>
      </div>
    );
  };

  const switchMonthlyValue = stringValue => {
    let stringValueString;
    if (typeof stringValue === 'object') {
      stringValueString = stringValue?.selectedItem;
      if (stringValueString === null) {
        return;
      } else {
        stringValueString = stringValueString.toString();
      }
    } else {
      stringValueString = stringValue.toString();
    }
    onChangePerformanceTerm(stringValueString === 'Quarterly');
  };

  return (
    <Container {...className('result-table-card')}>
      <Col {...className('card-container')}>
        <Row {...className('px-4')}>
          <div {...className('product-type')}>{productType}</div>
          <div {...className('share-class')}>{shareClass}</div>
        </Row>
        <Row {...className('investment-name')}>
          {fundName}
          {shouldShowTicker(fundTicker, shareClass) ? `[${fundTicker}]` : ''}
        </Row>
        <div {...className(isMobile ? 'column' : 'row')}>
          <Col>
            <Row {...className('row-container')}>
              <div {...className('header')}>Asset Class</div>
              <div {...className('value')}>{item[FIELDS.ASSETCLASS]}</div>
            </Row>
            <Row {...className('row-container')}>
              <div {...className('header')}>Avg. Annual Return 3Y</div>
              <div {...className('value')}>
                <span {...className(getTextValueColorClass(threeYearReturn))}>
                  <PerformanceValue
                    value={
                      showSalesChargeData ? threeYearReturn.salesCharge : threeYearReturn.standard
                    }
                    needTextColor={false}
                  />
                </span>
              </div>
            </Row>
          </Col>
          <Col>
            <Row {...className('row-container')}>
              <Row {...className('m-0')}>
                <div {...className('header')}>
                  Net / Gross Exp
                  <ButtonViewGlossary
                    className="no-print"
                    glossarySection="Gross / Net Expense Ratio"
                    isScreener
                    solid
                  />
                </div>
              </Row>
              {isSignatureResult ? (
                <span {...className('n-a-text')}>N/A</span>
              ) : (
                <div {...className('value')}>
                  <PerformanceValue value={item[FIELDS.TCNETEXPENSES]} isStyleTextColor={false} />/
                  <PerformanceValue value={item[FIELDS.TCGROSSEXPENSE]} isStyleTextColor={false} />
                  {showNetGrossDisclosure && (
                    <button {...className('glossary-btn', 'btn-reset')}>
                      <NetGrossDisclosureButton setModal={setModal} xid={xid} item={item} />
                    </button>
                  )}
                </div>
              )}
            </Row>
            <Row {...className('row-container')}>
              <Row {...className('m-0')}>
                <div {...className('header')}>
                  Morningstar Overall Rating<sup>10</sup>
                  <ButtonViewGlossary
                    className="no-print"
                    glossarySection="Overall Morningstar Rating"
                    isScreener
                    solid
                  />
                </div>
              </Row>
              <RatingRow
                outOfFunds={item[FIELDS.TCOVERALLRATINGOUFOFNUMBER]}
                rating={item[FIELDS.MSTARRATINGOVERALL]}
                category={item[FIELDS.TCMSTARCATEGORY]}
              />
            </Row>
          </Col>
        </div>
        <div {...className(isExpanded ? '' : 'd-none', isMobile ? 'column' : 'row')}>
          <Col>
            <Row {...className('row-container', 'border-0')}>
              <div>
                <div {...className('return-title')}>
                  Avg. Annualized Total Returns <sup>1, 2</sup>
                </div>
                <div {...className('as-of-date')}>
                  As of {format.oaDate(performanceAsOf)}
                  <ButtonViewGlossary className="no-print" glossarySection="" isScreener solid />
                </div>
              </div>
              <DropdownBasic items={performanceTerms} onChange={switchMonthlyValue}>
                <DropdownBasic.Trigger {...className('dropDown-trigger')}>
                  <Row {...className('trigger-container')}>
                    <div {...className('header')}>
                      {performanceQuarterly ? 'Quarterly' : 'Monthly'}
                      <Icon.ChevronDown {...className('stroke')} />
                    </div>
                  </Row>
                </DropdownBasic.Trigger>
                <DropdownBasic.Item {...className('position-relative')}>
                  {performanceTerm => (
                    <InteractiveElement
                      role="button"
                      {...className('text-decoration-none', 'stretched-link', 'text-black')}
                    >
                      {performanceTerm}
                    </InteractiveElement>
                  )}
                </DropdownBasic.Item>
              </DropdownBasic>
            </Row>
            <Row {...className('row-container', 'pt-2')}>
              <div {...className('header')}>1Y</div>
              {renderPerformanceElement(oneYearReturn, '1')}
            </Row>
            <Row {...className('row-container')}>
              <div {...className('header')}>3Y</div>
              {renderPerformanceElement(threeYearReturn, '3')}
            </Row>
            <Row {...className('row-container')}>
              <div {...className('header')}>5Y</div>
              {renderPerformanceElement(fiveYearReturn, '5')}
            </Row>
            <Row {...className('row-container')}>
              <div {...className('header')}>10Y</div>
              {renderPerformanceElement(tenYearReturn, '10')}
            </Row>
            <Row {...className('row-container')}>
              <div {...className('header')}>
                Since Inception<sup>3</sup>
              </div>
              <div {...className('value')}>
                <PerformanceValue
                  value={showSalesChargeData ? sinceIncep.salesCharge : sinceIncep.standard}
                />
                {(shareClass === 'Class R1' ||
                  shareClass === 'Class R2' ||
                  shareClass === 'Class R4') && <span {...className('text-dark')}>*</span>}
              </div>
            </Row>
            <Row {...className('row-container')}>
              <div {...className('header')}>
                Inception Date
                <ButtonViewGlossary
                  className="no-print"
                  glossarySection="Inception Date"
                  isScreener
                  solid
                />
              </div>
              <div>
                <div {...className('value')}>{inceptionDate}</div>
                {isShowUnderlyingFundIncepDate && (
                  <div {...className('value')}>{format.oaDate(underlyingFundIncepDate)}</div>
                )}
              </div>
            </Row>
          </Col>
          <Col>
            <FundDetailTable
              fundDetails={fundDetailTableData}
              fundType={fundType}
              assetClass={assetClass}
              isProp={isProp}
            />
            {tags && (
              <Row {...className('p-3')}>
                {tags.map((tag, index2) => {
                  return (
                    <Tag
                      data-key={`${tag.tag || ''}-${index2}`}
                      index={index2}
                      key={`${tag.tag || ''}-${index2}`}
                      label={tag.tag}
                      popoverMsg={tag.hoverText}
                    />
                  );
                })}
                <InteractiveElement
                  {...className('expand-tags-btn')}
                  onClick={() => setExpandTags(!expandTags)}
                >
                  {expandTags ? '- Show less' : '+ Show more'}
                </InteractiveElement>
              </Row>
            )}
            <FundDetailButtons
              setModal={setModal}
              fundType={fundType}
              xid={xid}
              item={item}
              isPlanInContext={isPlanInContext}
              restrictionText={restrictionText}
              fundName={fundName}
              fundTicker={fundTicker}
            />
          </Col>
        </div>
        <Row {...className('bottom-bar')}>
          <InteractiveElement
            as="Row"
            {...className('align-items-center')}
            onClick={() => onClickExpandRow(index)}
          >
            {isExpanded ? (
              <Icon.ChevronUp {...className('px-2', 'icon-color')} height="20px" width="20px" />
            ) : (
              <Icon.ChevronDown {...className('px-2', 'icon-color')} height="20px" width="20px" />
            )}
            <div {...className('btn-label')}>{isExpanded ? 'Collapse' : 'Expand'}</div>
          </InteractiveElement>
          <Row {...className('mb-2', 'align-items-center')}>
            <Button
              variant={'outline-button-primary'}
              {...className(
                'btn--rounded',
                'view-more-detail',
                'font-family-agenda',
                'd-flex',
                'align-items-center'
              )}
              size="sm"
              as="a"
              href={profileUrl}
              rel="noopener noreferrer"
              target="_blank"
              // TIAA Analytics Tracking
              data-analytics-linkname={`${fundName} [${fundTicker}]:InvestmentResults:View_fund_detail`}
              data-analytics-section="component"
              data-analytics-componenttype="Link"
              // END TIAA Analytics Tracking
            >
              View more detail
            </Button>
            {investButtonMode !== INVEST_BUTTON_MODES.hide &&
              channel !== CHANNELS.DYNAMICMICROSITES && (
                <InteractiveElement
                  {...className('invest-btn', 'screener-result-table-results-invest')}
                  // TIAA Analytics Tracking
                  data-analytics-linkname={`${fundName} [${fundTicker}]:InvestmentResults:Invest`}
                  data-analytics-section="component"
                  data-analytics-componenttype="Button"
                  // END TIAA Analytics Tracking
                  onClick={onClickInvestButton}
                >
                  Invest
                </InteractiveElement>
              )}
            {isPlanInContext && restrictionText.length > 0 && (
              <ButtonIconLink
                text="Restriction"
                onClick={e => {
                  e.stopPropagation();
                  setModal('Restriction', joinedRestrictions);
                }}
                variant="restriction"
                buttonClasses={['mr-1', 'ml-3', 'mt-4', 'mb-5']}
                textClass="btn-icon--fund-details"
                componentTag="Modal"
                fundName={fundName}
                fundTicker={fundTicker}
              />
            )}
          </Row>
        </Row>
      </Col>
      <ModalInvest
        show={isInvestModalVisible}
        onHide={handleCloseInvestModal}
        fundNameOrType={fundName}
      />
      <DocumentsModal
        onHide={() => setShowModal(false)}
        show={showModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
      />
    </Container>
  );
};

ResultTableCard.propTypes = {
  isExpanded: PropTypes.bool,
  item: PropTypes.object,
  index: PropTypes.number,
  isSignatureResult: PropTypes.bool,
  onClickExpandRow: PropTypes.func,
  performanceQuarterly: PropTypes.bool,
  participantHoldings: PropTypes.array,
  performanceAsOf: PropTypes.number,
  isSearchedPlanInContext: PropTypes.bool,
  isMobile: PropTypes.bool,
  onChangePerformanceTerm: PropTypes.func,
  searchedHoldings: PropTypes.array,
  showSalesCharge: PropTypes.bool,
  planInfo: PropTypes.array
};

ResultTableCard.defaultProps = {
  isSignatureResult: false
};

const mapStateToProps = state => {
  return {
    participantHoldings: state.filterReducer.participantHoldings,
    searchedHoldings: state.filterReducer.searchedHoldings,
    isSearchedPlanInContext: state.filterReducer.inputs.isSearchedPlanInContext,
    planInfo: state.filterReducer.planInfo
  };
};

export default connect(mapStateToProps, null)(ResultTableCard);
